import { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, Menu, Space, message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import CustomTable from "../../components/CustomTable/CustomTable";
import CustomCard from "../../components/CustomCard/CustomCard";
import TableHeader from "../../components/CustomTable/TableHeader";
import RejectDealModal from "../../components/RejectDealModal/RejectDealModal";
import useSearchableTable from "../../hooks/useSearchableTable";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDealsList,
  rejectJVDeal,
} from "../../redux/slices/JointVenturesDealsSlice";
import { count } from "../../redux/slices/dealsSlice";
import { count as refreshSidebarCount } from "../../redux/slices/AuthSlice";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import {
  DEAL_STATUS,
  DEAL_STATUS_LABEL_MAP,
  DEAL_APPROVAL_SUCCESS_MESSAGES,
  getDealApprovalSuccessMessage,
} from "../../constants/constants";
import { downloadPageHandle, formatDate } from "../../helpers/helpers";
import { safeStringSort, safeNumberSort, safeDateSort } from "../../helpers/sortHelpers";
import "./JointVenturesDeals.scss";
import {
  DEAL_JV_STATUS_PRIVILEGE_MAP,
  PERM_CODE,
} from "../../constants/PermissionCodes";
import useUserPrivileges from "../../hooks/useUserPrivileges";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../constants/tableColumnDefaults";

const IS_DEAL_STATUS_IDS = Object.values(DEAL_STATUS).map((status) => status.ID);
const DEAL_CATEGORY_ID = "DCT1000002";
const DEFAULT_JV_DEAL_STATUSES = [
  DEAL_STATUS.PENDING.ID,
  DEAL_STATUS.APPROVED.ID,
];

const formatIndianNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const numericValue = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(numericValue)) return value;

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const parseNumericValue = (value) => {
  const numericValue = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

const JointVenturesDeals = () => {
  const dispatch = useDispatch();
  const {
    deals = [],
    loading = false,
    rejectJVDealLoading = false,
  } = useSelector((state) => state.jointVenturesDeals || {});
  const { loading: docLoading, error: docError } = useSelector(
    (state) => state.documents || {},
  );
  const { statusList = [], loading: statusLoading = false } = useSelector(
    (state) => state.enqMngt || {},
  );
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [dealToReject, setDealToReject] = useState(null);
  const [columnControl, setColumnControl] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const { allowedStatusIds } = useUserPrivileges(DEAL_JV_STATUS_PRIVILEGE_MAP);
  const { CountData = [] } = useSelector((state) => state?.deals || {});

  const privileges = useMemo(
    () => user?.privilege?.map((p) => p.privilegeCode) || [],
    [user],
  );

  useEffect(() => {
    dispatch(count("DEALS"));
  }, [dispatch]);

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("deals"));
      if (res.payload?.statusCode === 200) {
        const preferred = DEFAULT_JV_DEAL_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        setSelectedStatus(
          preferred.length > 0
            ? preferred
            : allowedStatusIds.length > 0
              ? [allowedStatusIds[0]]
              : [],
        );
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds]);

  useEffect(() => {
    if (selectedStatus !== null && selectedStatus !== undefined) {
      dispatch(
        fetchDealsList({ selectedStatus, dealCategoryId: DEAL_CATEGORY_ID }),
      );
    }
  }, [dispatch, selectedStatus]);

  // Use API data as-is, add key for table
  const dataSource = Array.isArray(deals)
    ? deals.map((item, i) => ({ ...item, key: item.dealId || i + 1 }))
    : [];

  const {
    searchText,
    setSearchText,
    filteredData: searchFilteredData,
  } = useSearchableTable(dataSource);

  const filteredStatusList = useMemo(() => {
    const augmentedStatusList = (statusList || []).slice();
    
    return augmentedStatusList.filter((s) =>
      allowedStatusIds.includes(s.statusId),
    );
  }, [statusList, allowedStatusIds]);

  const statusListWithCounts = useMemo(
    () =>
      filteredStatusList.map((item) => {
        const isDeal = IS_DEAL_STATUS_IDS.includes(item.statusId);
        const matchedCount = CountData?.find(
          (countItem) =>
            countItem.statusId === item.statusId &&
            countItem.dealCategoryId === DEAL_CATEGORY_ID,
        );

        return {
          ...item,
          count: isDeal ? Number(matchedCount?.count || 0) : undefined,
        };
      }),
    [filteredStatusList, CountData],
  );

  const selectedStatusIds = useMemo(() => {
    if (!selectedStatus) return [];
    return Array.isArray(selectedStatus) ? selectedStatus : [selectedStatus];
  }, [selectedStatus]);

  const selectedStatusLabels = useMemo(() => {
    return selectedStatusIds.map((id) =>
      String(DEAL_STATUS_LABEL_MAP[id] || id).trim().toLowerCase(),
    );
  }, [selectedStatusIds]);

  // Apply deals status filter on top of search filter
  const filteredData = useMemo(() => {
    if (Array.isArray(selectedStatus) && selectedStatus.length === 0) return [];
    if (selectedStatusIds.length === 0) return searchFilteredData;
    return searchFilteredData.filter((item) => {
      const dealStatus = String(item?.dealStatus || "").trim().toLowerCase();
      const statusId = String(item?.statusId || "").trim().toLowerCase();
      return (
        selectedStatusIds.some(
          (id) =>
            String(id).toLowerCase() === dealStatus ||
            String(id).toLowerCase() === statusId,
        ) || selectedStatusLabels.includes(dealStatus)
      );
    });
  }, [searchFilteredData, selectedStatus, selectedStatusIds, selectedStatusLabels]);

  const selectedStatusCount = useMemo(() => {
    if (Array.isArray(selectedStatus)) {
      return statusListWithCounts
        .filter((status) => selectedStatus.includes(status.statusId))
        .reduce((sum, item) => sum + Number(item.count || 0), 0);
    }
    return (
      statusListWithCounts.find((status) => status.statusId === selectedStatus)
        ?.count || 0
    );
  }, [statusListWithCounts, selectedStatus]);

  const handleDownload = (type) =>
    downloadPageHandle({
      dispatch,
      type,
      screenName: "deals",
      dealType: "Joint Ventures",
      dealCategoryId: "DCT1000002",
      statusId: selectedStatus,
    });

  useEffect(() => {
    if (docError) {
      message.error(docError || "Download failed");
    }
  }, [docError]);

  const handleOpenRejectModal = useCallback((record) => {
    setDealToReject(record || null);
    setRejectReason("");
    setIsRejectModalOpen(true);
  }, []);

  const handleRejectDeal = async () => {
    const dealId = dealToReject?.dealId;
    if (!dealId) {
      message.error("Deal ID is missing");
      return;
    }

    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      message.error("Please enter reason for reject");
      return;
    }

    const result = await dispatch(
      rejectJVDeal({ dealId, comments: trimmedReason }),
    );
    if (result?.meta?.requestStatus !== "fulfilled") {
      const errorMessage =
        result?.payload?.message ||
        result?.payload?.error ||
        result?.payload ||
        "Failed to reject deal. Please try again.";
      message.error(`Deal ${dealId}: ${errorMessage}`);
      return;
    }

    message.success(
      getDealApprovalSuccessMessage(
        DEAL_APPROVAL_SUCCESS_MESSAGES.DEAL_REJECTED,
        dealId,
      ),
    );
    setIsRejectModalOpen(false);
    setRejectReason("");
    setDealToReject(null);

    setSelectedStatus(DEAL_STATUS.REJECTED.ID);
    dispatch(
      fetchDealsList({
        selectedStatus: DEAL_STATUS.REJECTED.ID,
        dealCategoryId: DEAL_CATEGORY_ID,
      }),
    );
    dispatch(count("DEALS"));
    dispatch(refreshSidebarCount("ALL"));
  };

  // Action menu with navigation for Send Questionnaire
  const getActionMenu = (record, navigate, onRejectDeal, rejectLoading) => {
    const isJointVenture = record?.dealCategory === "Joint Ventures";
    const normalizedStatus = String(record?.dealStatus || "")
      .trim()
      .toLowerCase();
    const isApproved = normalizedStatus === "approved";
    const isCancelled = normalizedStatus === "cancelled";
    const isViewOnlyStatus = isApproved || isCancelled;

    return (
      <Menu>
        {record.dealCategory === "Joint Ventures" &&
          !isViewOnlyStatus &&
          privileges.includes(PERM_CODE.DMSQB) && (
            <Menu.Item
              className="send-btn"
              key="send-questionnaire"
              icon={<SendOutlined />}
              onClick={() => {
                const jvDealCategoryId =
                  record.dealCategoryId || "DCT1000002";
                navigate(
                  `/enquiry-management/send-questionnaire/${record.orgId}?fromDeals=true`,
                  {
                    state: {
                      fromDeals: true,
                      dealId: record.dealId,
                      dealCategoryId: jvDealCategoryId,
                      org: {
                        orgId: record.orgId,
                        dealCategoryId: jvDealCategoryId,
                      },
                    },
                  },
                );
              }}
            >
              Send Questionnaire
            </Menu.Item>
          )}
        {isJointVenture && record?.isSendQuestionnaire && !isViewOnlyStatus && (
          <Menu.Item
            className="approve-btn"
            key="approve"
            icon={<CheckCircleOutlined />}
            onClick={() => {
              if (record?.dealId) {
                navigate(
                  `/deal-approval?dealId=${encodeURIComponent(record.dealId)}`,
                  {
                    state: { dealId: record.dealId, dealData: record },
                  },
                );
              }
            }}
          >
            Approve
          </Menu.Item>
        )}
        {isJointVenture && isViewOnlyStatus && (
          <Menu.Item
            key="view"
            style={{ fontSize: "small" }}
            icon={<EyeOutlined />}
            onClick={() => {
              if (record?.dealId) {
                navigate(
                  `/joint-ventures-view-details?dealId=${encodeURIComponent(record.dealId)}`,
                  {
                    state: { dealId: record.dealId, dealData: record },
                  },
                );
              }
            }}
          >
            View
          </Menu.Item>
        )}
        {!isViewOnlyStatus && privileges.includes(PERM_CODE.DPSRE) && (
          <Menu.Item
            className="reject-btn"
            key="reject"
            icon={<CloseCircleOutlined />}
            disabled={!record?.dealId || rejectLoading}
            onClick={() => onRejectDeal(record)}
          >
            Reject
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Deal ID",
        dataIndex: "dealId",
        key: "dealId",
        sorter: (a, b) => safeStringSort(a?.dealId, b?.dealId),
      },
      {
        title: "Business Name",
        dataIndex: "orgName",
        key: "orgName",
        sorter: (a, b) => safeStringSort(a?.orgName, b?.orgName),
      },
      {
        title: "Deal Status",
        dataIndex: "dealStatus",
        key: "dealStatus",
        sorter: (a, b) => safeStringSort(a?.dealStatus, b?.dealStatus),
        render: (text) => <span className="business-status-badge">{text}</span>,
      },
      {
        title: "Project Name",
        dataIndex: "dealName",
        key: "dealName",
        sorter: (a, b) => safeStringSort(a?.dealName, b?.dealName),
        render: (text, record) =>
          record.dealCategory === "Invoice Financing" ? "N/A" : text,
      },
      {
        title: "ROI %",
        dataIndex: "roi",
        key: "roi",
        sorter: (a, b) => safeNumberSort(a?.roi, b?.roi),
      },
      {
        title: "Duration",
        dataIndex: "dealDuration",
        key: "dealDuration",
        sorter: (a, b) =>
          safeNumberSort(a?.dealDuration || a?.tenure, b?.dealDuration || b?.tenure),
        render: (value, record) => value || record.tenure || "N/A",
      },
      {
        title: "Project Value",
        dataIndex: "dealValue",
        key: "dealValue",
        sorter: (a, b) => safeNumberSort(a?.dealValue, b?.dealValue),
        render: (value) => formatIndianNumber(value),
      },
      {
        title: "Submitted Date",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: (a, b) => safeDateSort(a?.createdAt, b?.createdAt),
        render: (value) => formatDate(value),
      },
      {
        title: "Action",
        key: "action",
        fixed: "right",
        width: 140,
        render: (_, record) => {
          const isRejectedJV =
            String(record?.dealStatus || "").toLowerCase() === "rejected" &&
            record?.dealCategory === "Joint Ventures";
          if (isRejectedJV) return <span>-</span>;
          return (
            <Space size="small">
              <Dropdown
                overlay={getActionMenu(
                  record,
                  navigate,
                  handleOpenRejectModal,
                  rejectJVDealLoading,
                )}
                trigger={["click"]}
                placement="bottomRight"
              >
                <MoreOutlined
                  style={{ fontSize: "18px", cursor: "pointer" }}
                />
              </Dropdown>
            </Space>
          );
        },
      },
    ],
    // getActionMenu is defined in-component; depend on values it closes over
    [navigate, handleOpenRejectModal, rejectJVDealLoading, privileges],
  );

  return (
    <>
      <CustomCard>
        <TableHeader
          title={`JOINT VENTURES DEALS LIST (${selectedStatus ? selectedStatusCount : filteredData.length})`}
          searchText={searchText}
          setSearchText={setSearchText}
          isDropdown={true}
          dropdownData={statusListWithCounts}
          selectedStatus={selectedStatus}
          handleStatusChange={setSelectedStatus}
          statusDisplayNames={DEAL_STATUS_LABEL_MAP}
          statusLoading={statusLoading && statusList.length === 0}
          isDownloadRequired={true}
          onDownloadPdf={() => handleDownload("pdf")}
          onDownloadExcel={() => handleDownload("excel")}
          downloadLoading={docLoading}
          columnControl={columnControl}
        />
        <CustomTable
          columns={tableColumns}
          dataSource={filteredData}
          loading={loading}
          rowKey="dealId"
          screenKey={buildTableScreenKey("jvDeals")}
          defaultColumnKeys={TABLE_COLUMN_DEFAULTS.jvDeals}
          onColumnControlChange={setColumnControl}
        />
      </CustomCard>

      <RejectDealModal
        open={isRejectModalOpen}
        onCancel={() => {
          setIsRejectModalOpen(false);
          setRejectReason("");
          setDealToReject(null);
        }}
        onReject={handleRejectDeal}
        loading={rejectJVDealLoading}
        reason={rejectReason}
        onReasonChange={setRejectReason}
      />
    </>
  );
};

export default JointVenturesDeals;
